/** Notifications + event catalog → `TimelineModel`. Pure, no React, no fetching.
 *
 *  This is the whole contract of the feature: everything the timeline renders
 *  is decided here, so the components stay dumb and the AI-proposal case gets
 *  the same layout for free. Unit-tested in `buildTimeline.test.ts`.
 *
 *  ## Slots
 *
 *  A slot is a *moment* — `(anchor, direction, offset)` — not an event code.
 *  `tour_participant.trip_flag_event_72h`, `order.72h` and `departure.72h` all
 *  describe "72h before trip start", so they share one slot and appear side by
 *  side. That is the point of a single timeline: the same message configured on
 *  both the booking-level and the order-level event becomes visible instead of
 *  hiding on two separate screens.
 *
 *  ## Bands
 *
 *  Action-based events have no position on a time axis, so pretending
 *  otherwise would be a lie — they get their own band above the spine, ordered
 *  by the lifecycle reading order the backend assigns. `beat.*` gets a third
 *  band: its offsets live on the notification and its anchor is a per-tour
 *  itinerary point, so it has no tenant-global position either.
 */
import { eventDisplayLabel, labelIsFamilyName } from './labels';
import type {
  Band,
  EmptyRun,
  Phase,
  TimelineGroup,
  EventPosition,
  TimelineBand,
  TimelineEventNode,
  TimelineModel,
  TimelineNotification,
  TimelineRow,
  TimelineSlot,
} from './types';

export interface CatalogEntry {
  code: string;
  family: string;
  label: string | null;
  position: EventPosition;
  canFire?: boolean | null;
  cannotFireReason?: TimelineEventNode['cannotFireReason'];
  canonicalCode?: string | null;
}

export interface BuildOptions {
  /** Catalog entries used to render *empty* moments. Omit for a proposal-only
   *  view, where showing every unused slot would be noise. */
  catalog?: CatalogEntry[];
  tenant?: TimelineModel['tenant'];
  generatedTs?: string;
  statsAvailable?: boolean;
  /** Restrict to these families; empty/undefined means all. */
  families?: string[];
  /** Hide moments that have no notifications entirely (rather than collapsing). */
  hideEmpty?: boolean;
  /** Bands to drop completely. Their notifications are excluded from the model
   *  too, so the counters never describe rows nobody can see — the count of
   *  what was dropped is reported back in `hiddenNotificationCounts`. */
  hiddenBands?: Band[];
}

const BAND_ORDER: Band[] = ['reactive', 'lifecycle', 'itinerary', 'unknown'];

const BAND_LABELS: Record<Band, string> = {
  reactive: 'When something happens',
  lifecycle: 'Along the trip',
  itinerary: 'Itinerary beats',
  unknown: 'Unrecognised events',
};

/** Family order on the lifecycle band, per the plan's §2.1 Family axis.
 *  Unlisted families sort after these, alphabetically. */
export const FAMILY_ORDER = [
  'tour_participant',
  'order',
  'departure',
  'user_departure',
  'tour_departure',
  'booking',
  'user',
  'tour_operator',
  'beat',
];

function familyRank(family: string): number {
  const i = FAMILY_ORDER.indexOf(family);
  return i === -1 ? FAMILY_ORDER.length : i;
}

/** The lifecycle band's sections, in journey order. These headings replace the
 *  BOOKING / TRIP START / TRIP END markers: naming the phase says the same
 *  thing and survives a family that skips a phase entirely. */
const PHASE_SECTIONS: { phase: Phase; label: string; hint: string }[] = [
  { phase: 'postbooking', label: 'Post-booking', hint: 'after the booking is made' },
  { phase: 'pretrip', label: 'Pre-trip', hint: 'before trip start' },
  { phase: 'ontrip', label: 'On-trip', hint: 'after trip start' },
  { phase: 'endtrip', label: 'Pre-trip-end', hint: 'before trip end' },
  { phase: 'posttrip', label: 'Post-trip', hint: 'after trip end' },
];

/** A lifecycle row is identified by (family, moment) — the timeline groups by
 *  family first, so two families at the same instant are two rows, not one row
 *  with two nodes. Bands with no time axis keep one slot per band. */
export function slotKey(position: EventPosition): string {
  if (position.offsetSeconds === null) return `${position.band}:${position.phase}`;
  return `${position.family}:${position.anchor}:${position.direction}:${position.offsetSeconds}`;
}

function comparePositions(a: EventPosition, b: EventPosition): number {
  return a.sortKey[0] - b.sortKey[0] || a.sortKey[1] - b.sortKey[1];
}

/** Human label for a slot: the offset phrase, or the event name for reactive. */
function slotLabel(position: EventPosition): string {
  return position.labelLong;
}

export function buildTimeline(
  notifications: TimelineNotification[],
  options: BuildOptions = {},
): TimelineModel {
  const familyFilter = options.families?.length ? new Set(options.families) : null;
  const visible = familyFilter
    ? notifications.filter((n) => familyFilter.has(n.position?.family ?? familyOf(n.event)))
    : notifications;

  // --- 1. index every event code we know about ------------------------------
  // Catalog first (so empty moments exist), then anything a notification
  // references that the catalog doesn't know — a freeSolo event code, or a
  // proposal inventing one. Dropping those silently is how an overview starts
  // lying, so they get a node too.
  const nodes = new Map<string, TimelineEventNode>();

  const addNode = (entry: CatalogEntry) => {
    if (nodes.has(entry.code)) return;
    if (familyFilter && !familyFilter.has(entry.family)) return;
    nodes.set(entry.code, {
      code: entry.code,
      family: entry.family,
      label: eventDisplayLabel(entry),
      labelIsFamilyName: labelIsFamilyName(entry),
      position: entry.position,
      notifications: [],
      canFire: entry.canFire,
      cannotFireReason: entry.cannotFireReason,
      canonicalCode: entry.canonicalCode,
    });
  };

  for (const entry of options.catalog ?? []) addNode(entry);

  for (const nf of visible) {
    if (!nodes.has(nf.event)) {
      addNode({
        code: nf.event,
        family: nf.position?.family ?? familyOf(nf.event),
        label: null,
        position: nf.position ?? unknownPosition(nf.event),
        canFire: nf.canFire,
        cannotFireReason: nf.cannotFireReason,
        canonicalCode: nf.canonicalEvent,
      });
    }
    nodes.get(nf.event)!.notifications.push(nf);
  }

  // --- 2. group nodes into slots -------------------------------------------
  const slots = new Map<string, TimelineSlot>();
  for (const node of nodes.values()) {
    const key = slotKey(node.position);
    let slot = slots.get(key);
    if (!slot) {
      slot = {
        key,
        position: node.position,
        nodes: [],
        empty: true,
        notificationCount: 0,
      };
      slots.set(key, slot);
    }
    slot.nodes.push(node);
    slot.notificationCount += node.notifications.length;
    if (node.notifications.length) slot.empty = false;
  }

  for (const slot of slots.values()) {
    // Hiding unused events means unused EVENT NODES, not just wholly-unused
    // moments. The catalog is always loaded (it supplies the human labels), so
    // every moment carries a node per family — a slot with one configured
    // notification would otherwise still render the four empty sibling
    // families, and the switch would look like it did nothing.
    if (options.hideEmpty) {
      slot.nodes = slot.nodes.filter((node) => node.notifications.length > 0);
    }
    // Families read left-to-right in a stable order inside the slot; nodes with
    // notifications first so a busy slot leads with its content.
    slot.nodes.sort(
      (a, b) =>
        b.notifications.length - a.notifications.length ||
        a.family.localeCompare(b.family) ||
        a.code.localeCompare(b.code),
    );
    for (const node of slot.nodes) {
      node.notifications.sort(sortNotifications);
    }
  }

  // --- 3. split into bands, then into family groups -------------------------
  const hidden = new Set(options.hiddenBands ?? []);
  const hiddenNotificationCounts: Partial<Record<Band, number>> = {};
  const bands: TimelineBand[] = [];
  for (const band of BAND_ORDER) {
    if (hidden.has(band)) {
      const n = [...slots.values()]
        .filter((s) => s.position.band === band)
        .reduce((sum, s) => sum + s.notificationCount, 0);
      if (n) hiddenNotificationCounts[band] = n;
      continue;
    }
    const bandSlots = [...slots.values()]
      .filter((s) => s.position.band === band)
      .filter((s) => !(options.hideEmpty && s.empty));
    if (!bandSlots.length) continue;

    // The lifecycle band is sectioned by PHASE — post-booking, pre-trip,
    // on-trip, pre-trip-end, post-trip — and each section lists (family, time).
    const groups: TimelineGroup[] =
      band === 'lifecycle' ? phaseGroups(bandSlots) : [singleGroup(band, bandSlots)];

    bands.push({
      band,
      label: BAND_LABELS[band],
      groups,
      notificationCount: bandSlots.reduce((n, s) => n + s.notificationCount, 0),
    });
  }

  const shown = hidden.size
    ? visible.filter((n) => !hidden.has(n.position?.band ?? unknownPosition(n.event).band))
    : visible;

  return {
    bands,
    notifications: shown,
    hiddenNotificationCounts,
    tenant: options.tenant,
    generatedTs: options.generatedTs,
    statsAvailable: options.statsAvailable,
    hasProposals: shown.some((n) => !n.materialized),
  };
}

// --- helpers ----------------------------------------------------------------

function phaseGroups(bandSlots: TimelineSlot[]): TimelineGroup[] {
  const byPhase = new Map<string, TimelineSlot[]>();
  for (const slot of bandSlots) {
    const list = byPhase.get(slot.position.phase);
    if (list) list.push(slot);
    else byPhase.set(slot.position.phase, [slot]);
  }

  const groups: TimelineGroup[] = [];
  for (const section of PHASE_SECTIONS) {
    const slotsOfPhase = byPhase.get(section.phase);
    if (!slotsOfPhase?.length) continue;
    // Within a phase: family first (plan order), then the moment. The moment
    // comparison is the band-wide sort key, which already counts pre-trip down
    // and post-trip up.
    const sorted = slotsOfPhase.sort(
      (a, b) =>
        familyRank(a.position.family) - familyRank(b.position.family) ||
        a.position.family.localeCompare(b.position.family) ||
        comparePositions(a.position, b.position),
    );
    groups.push({
      key: `lifecycle:${section.phase}`,
      phase: section.phase,
      label: section.label,
      hint: section.hint,
      rows: collapseEmptyRuns(sorted),
      milestones: {},
      notificationCount: sorted.reduce((n, s) => n + s.notificationCount, 0),
    });
  }
  return groups;
}

function singleGroup(band: Band, bandSlots: TimelineSlot[]): TimelineGroup {
  const sorted = [...bandSlots].sort((a, b) => comparePositions(a.position, b.position));
  return {
    key: band,
    rows: sorted.map(asRow),
    milestones: {},
    notificationCount: sorted.reduce((n, s) => n + s.notificationCount, 0),
  };
}

function asRow(slot: TimelineSlot): TimelineRow {
  return { kind: 'slot', ...slot };
}

/** Most moments have nothing configured. Collapse consecutive empty ones into
 *  a single expandable row so the populated ones stay readable. */
function collapseEmptyRuns(slots: TimelineSlot[]): TimelineRow[] {
  const rows: TimelineRow[] = [];
  let run: TimelineSlot[] = [];

  const flush = () => {
    if (!run.length) return;
    // (runPhase is reset implicitly: the next push sets it.)
    // A single empty moment is not worth a "show 1 more" affordance.
    if (run.length === 1) {
      rows.push(asRow(run[0]!));
    } else {
      rows.push({
        kind: 'empty-run',
        key: `empty:${run[0]!.key}:${run[run.length - 1]!.key}`,
        slots: run,
        fromLabel: slotLabel(run[0]!.position),
        toLabel: slotLabel(run[run.length - 1]!.position),
      } satisfies EmptyRun);
    }
    run = [];
  };

  let runKey: string | null = null;
  for (const slot of slots) {
    if (slot.empty) {
      // A run must never span two phases or two families: its from→to label
      // reads as one stretch of the journey, and "Order 30d → Departure 1h"
      // is not one stretch of anything.
      const key = `${slot.position.phase}:${slot.position.family}`;
      if (run.length && key !== runKey) flush();
      run.push(slot);
      runKey = key;
    } else {
      flush();
      rows.push(asRow(slot));
    }
  }
  flush();
  return rows;
}


function sortNotifications(a: TimelineNotification, b: TimelineNotification): number {
  // Grouped the way ops reads them: always-on first, then defaults, then the
  // tour-specific overrides (which the UI collapses).
  const mode = (n: TimelineNotification) => n.tourOverrideMode ?? 1;
  return (
    mode(a) - mode(b) ||
    Number(b.enabled ?? false) - Number(a.enabled ?? false) ||
    a.name.localeCompare(b.name)
  );
}

export function familyOf(eventCode: string): string {
  return eventCode.split('.', 1)[0] ?? '';
}

/** Fallback position for a code no catalog entry covers. */
export function unknownPosition(eventCode: string): EventPosition {
  return {
    phase: 'unknown',
    band: 'unknown',
    anchor: 'none',
    direction: 'at',
    offsetSeconds: null,
    offsetConfigurable: false,
    family: familyOf(eventCode),
    evid: null,
    labelShort: eventCode,
    labelLong: eventCode,
    sortKey: [30, 0],
  };
}

/** Every family present in a model, for the toolbar's filter chips. */
export function familiesIn(model: TimelineModel): string[] {
  const out = new Set<string>();
  for (const band of model.bands) {
    for (const group of band.groups) {
      for (const row of group.rows) {
        const slots = row.kind === 'slot' ? [row] : row.slots;
        for (const slot of slots) for (const node of slot.nodes) out.add(node.family);
      }
    }
  }
  return [...out].sort((a, b) => familyRank(a) - familyRank(b) || a.localeCompare(b));
}
